import { PayloadAction } from '@reduxjs/toolkit';

import { getBrowserName, isMobile, isIOSDevice, isSafari } from 'server/helpers/deviceInfo';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { saleSelectors } from 'store/sale/selectors';
import { sliceFactory, thunkActionFactory } from 'store/utils';
import { IBusinessLogic } from 'types/BusinessLogic';

export interface ChatState {
  userPrompt: string;
  messages: Array<{
    sender: 'user' | 'system';
    text: string;
    actionList?: string[];
  }>;
  isLoading: boolean;
}

const initialState: ChatState = {
  userPrompt: '',
  messages: [],
  isLoading: false,
};

const { actions, reducer } = sliceFactory({
  name: 'chat',
  initialState,
  reducers: {
    setUserPrompt: (state, action: PayloadAction<string>) => {
      state.userPrompt = action.payload;
    },
    addMessage: (
      state,
      action: PayloadAction<{ sender: 'user' | 'system'; text: string; actionList?: string[] }>
    ) => {
      state.messages.push(action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const { setUserPrompt, addMessage, setLoading } = actions;

export const sendChatMessage = thunkActionFactory<IBusinessLogic>(
  async ({ dispatch, state, businessLogic }) => {
    const { userPrompt } = state.chat;
    const { wallet, payment, auth, sale, companyInfo } = state;
    const userAgent = auth.userAgent || window?.navigator?.userAgent;

    if (!userPrompt.trim()) return;

    try {
      dispatch(setLoading(true));
      dispatch(addMessage({ sender: 'user', text: userPrompt }));
      const response = await businessLogic.chat.fetchChatAnswer({
        userPrompt,
        enabledPaymentMethodList: wallet?.enabledPaymentMethods ?? [],
        isPartialPaymentEnabled: payment?.partialPaymentEnabled ?? false,
        browser: getBrowserName(userAgent),
        device: isMobile(userAgent)
          ? isIOSDevice(userAgent)
            ? 'iOS Mobile'
            : 'Mobile'
          : isSafari(userAgent)
          ? 'Safari'
          : 'Other',
        isAutopayEligible: sale?.scheduleInfo?.autoPayEligible ?? false,
        isAutopaySetup: payment?.isAutoPayOn ?? false,
        hasAchConvenienceFee: saleSelectors?.achOnlineConvenienceFeeEnabledSelector(sale) ?? false,
        achConvenienceFee: saleSelectors?.achOnlineConvenienceFeeAmountSelector(sale) ?? 0,
        merchantName: companyInfoSelectors?.nameSelector(companyInfo) ?? '',
      });

      if (response?.answer) {
        dispatch(
          addMessage({
            sender: 'system',
            text: response.answer,
            actionList: response.actionList,
          })
        );
      } else {
        dispatch(
          addMessage({
            sender: 'system',
            text: 'I encountered an error processing your request. Please try again.',
          })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          sender: 'system',
          text: 'I encountered an error processing your request. Please try again.',
        })
      );
    } finally {
      dispatch(setLoading(false));
      dispatch(setUserPrompt(''));
    }
  }
);

export const chatReducer = reducer;
